import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, updateDoc, doc, limit, getDoc, arrayUnion } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { useSchoolId } from '../lib/auth/schoolContext';
import { useUserRole, ROLES, hasRole } from '../lib/auth/roles';

const SessionLogForm = ({ interventionId, onSessionLogged }) => {
  const { schoolId } = useSchoolId();
  const [intervention, setIntervention] = useState(null);
  const { students: allStudents, loading: studentsLoading } = useStudents();
  const [students, setStudents] = useState([]);
  const { role, loading: roleLoading } = useUserRole();
  const [eligibleDate, setEligibleDate] = useState(null);
  const [existingEligibleLog, setExistingEligibleLog] = useState(null);
  const [sessionData, setSessionData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    duration: '',
    attendance: [],
    activities: '',
    progress: '',
    challenges: '',
    nextSteps: '',
    notes: '',
    // Per-student per-target scoring for this session
    targetScores: []
  });

  useEffect(() => {
    if (interventionId) {
      fetchIntervention();
    }
  }, [interventionId, schoolId]);

  const fetchIntervention = async () => {
    try {
      const snap = await getDoc(doc(db, 'interventions', interventionId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        if (data.schoolId && schoolId && data.schoolId !== schoolId) {
          toast.error('You do not have access to this intervention');
          setIntervention(null);
          return;
        }
        setIntervention(data);
      } else {
        setIntervention(null);
      }
    } catch (error) {
      console.error('Error fetching intervention:', error);
      toast.error('Failed to fetch intervention details');
    }
  };

  // When global students or intervention change, compute the relevant students and initialize attendance
  useEffect(() => {
    if (intervention && !studentsLoading) {
      const valid = (intervention.students || [])
        .map(id => allStudents.find(s => s.id === id))
        .filter(Boolean);
      setStudents(valid);
      setSessionData(prev => ({
        ...prev,
        attendance: valid.map(student => ({
          studentId: student.id,
          present: true,
          name: `${student.firstName} ${student.lastName}`
        })),
        targetScores: valid.map(student => ({
          studentId: student.id,
          name: `${student.firstName} ${student.lastName}`,
          scores: (intervention.targets || []).slice(0, 3).map(t => ({
            targetId: t.id,
            targetText: t.text,
            status: 'not_met'
          }))
        }))
      }));
    }
  }, [intervention, allStudents, studentsLoading]);

  // Determine the eligible session date this week (based on intervention.sessionSchedule)
  useEffect(() => {
    const checkEligibility = async () => {
      if (!intervention) { setEligibleDate(null); setExistingEligibleLog(null); return; }
      const schedule = Array.isArray(intervention.sessionSchedule) ? intervention.sessionSchedule : [];
      if (!schedule.length) { setEligibleDate(null); setExistingEligibleLog(null); return; }
      const today = new Date();
      const day = today.getDay(); // 0 Sun ... 6 Sat
      const monday = new Date(today);
      const diffToMonday = (day === 0 ? -6 : 1 - day);
      monday.setDate(today.getDate() + diffToMonday);
      monday.setHours(0,0,0,0);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23,59,59,999);
      let match = null;
      for (const dStr of schedule) {
        const d = new Date(dStr);
        if (d >= monday && d <= sunday) { match = dStr; break; }
      }
      setEligibleDate(match);
      if (match) {
        try {
          const logsQ = query(
            collection(db, 'sessionLogs'),
            where('schoolId', '==', schoolId),
            where('interventionId', '==', interventionId),
            where('date', '==', match),
            limit(1)
          );
          const snap = await getDocs(logsQ);
          setExistingEligibleLog(!snap.empty ? { id: snap.docs[0].id, ...snap.docs[0].data() } : null);
        } catch (e) {
          console.error('Error checking existing session log', e);
        }
      } else {
        setExistingEligibleLog(null);
      }
    };
    checkEligibility();
  }, [intervention, interventionId, schoolId]);

  const handleAttendanceChange = (studentId, present) => {
    setSessionData(prev => ({
      ...prev,
      attendance: prev.attendance.map(att => 
        att.studentId === studentId ? { ...att, present } : att
      )
    }));
  };

  const handleTargetScoreChange = (studentId, targetId, status) => {
    setSessionData(prev => ({
      ...prev,
      targetScores: prev.targetScores.map(ts =>
        ts.studentId === studentId
          ? {
              ...ts,
              scores: ts.scores.map(s => s.targetId === targetId ? { ...s, status } : s)
            }
          : ts
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Enforce schedule & role: only SENCOs can override
    if (!roleLoading && !hasRole(role, ROLES.SENCO)) {
      if (!eligibleDate) {
        toast.error('No scheduled session to log this week.');
        return;
      }
      if (sessionData.date !== eligibleDate) {
        toast.error('You can only log this week\'s scheduled session.');
        return;
      }
      if (existingEligibleLog) {
        toast.error('This session is already logged. Contact SENCO to amend.');
        return;
      }
    }
    
    if (!sessionData.duration || !sessionData.activities) {
      toast.error('Please fill in all required fields');
      return;
    }

    let sessionDoc;
    try {
      // Create the session log — this is the canonical source of truth.
      // Everything downstream (dashboards, progress views, evidence packs)
      // queries `sessionLogs` by `interventionId`, so once this addDoc
      // succeeds the session is logged regardless of any denormalisation
      // failures that follow.
      sessionDoc = await addDoc(collection(db, 'sessionLogs'), {
        interventionId,
        interventionName: intervention?.name || '',
        studentIds: (sessionData.attendance || [])
          .filter((att) => att.present)
          .map((att) => att.studentId),
        ...sessionData,
        // Only keep target scores for present students
        targetScores: (sessionData.targetScores || []).filter(ts => (sessionData.attendance || []).find(a => a.studentId === ts.studentId && a.present)),
        createdRole: role || 'UNKNOWN',
        amendsLogId: (hasRole(role, ROLES.SENCO) && existingEligibleLog && sessionData.date === existingEligibleLog.date) ? existingEligibleLog.id : null,
        createdAt: new Date(),
        createdBy: 'current-user', // TODO: Get from auth context
        status: 'completed',
        schoolId,
      });
    } catch (error) {
      console.error('Error logging session:', error);
      toast.error('Failed to log session');
      return;
    }

    // Denormalised counter on the intervention doc. Best-effort only:
    // some interventions may be misconfigured (e.g. SENCO added 9 students
    // when the cohort cap is 8) which can cause the intervention update to
    // fail under the cohort-size rule. The session log above is already
    // safe, so we surface the warning quietly and don't fail the TA.
    if (intervention) {
      try {
        const interventionRef = doc(db, 'interventions', interventionId);
        await updateDoc(interventionRef, {
          sessions: arrayUnion(sessionDoc.id),
        });
      } catch (denormErr) {
        console.warn(
          'Session logged, but failed to update intervention.sessions denormalisation',
          denormErr,
        );
      }
    }

    toast.success('Session logged successfully!');

    setSessionData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      duration: '',
      attendance: students.map(student => ({
        studentId: student.id,
        present: true,
        name: `${student.firstName} ${student.lastName}`
      })),
      activities: '',
      progress: '',
      challenges: '',
      nextSteps: '',
      notes: '',
      targetScores: students.map(student => ({
        studentId: student.id,
        name: `${student.firstName} ${student.lastName}`,
        scores: (intervention?.targets || []).slice(0, 3).map(t => ({
          targetId: t.id,
          targetText: t.text,
          status: 'not_met'
        }))
      }))
    });

    if (onSessionLogged) {
      onSessionLogged();
    }
  };

  if (!intervention) {
    return <div className="text-center py-8">Loading intervention details...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">
        Log Session: {intervention.name}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Session Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date *
            </label>
            <input
              type="date"
              value={sessionData.date}
              onChange={(e) => setSessionData(prev => ({ ...prev, date: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={!hasRole(role, ROLES.SENCO) && Boolean(eligibleDate)}
            />
            {eligibleDate ? (
              <p className="text-xs text-gray-500 mt-1">Scheduled this week: {eligibleDate}{existingEligibleLog ? ' (already logged)' : ''}</p>
            ) : (
              <p className="text-xs text-amber-600 mt-1">No scheduled session this week for this intervention.</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time *
            </label>
            <input
              type="time"
              value={sessionData.time}
              onChange={(e) => setSessionData(prev => ({ ...prev, time: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Duration *
            </label>
            <input
              type="text"
              value={sessionData.duration}
              onChange={(e) => setSessionData(prev => ({ ...prev, duration: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 45 minutes"
              required
            />
          </div>
        </div>

        {/* Attendance */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attendance
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sessionData.attendance.map((att) => (
              <label key={att.studentId} className="flex items-center space-x-2 p-3 border border-gray-200 rounded-md hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={att.present}
                  onChange={(e) => handleAttendanceChange(att.studentId, e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <span className="text-sm text-gray-700">{att.name}</span>
                <span className={`ml-auto text-xs px-2 py-1 rounded ${
                  att.present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {att.present ? 'Present' : 'Absent'}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Target Scoring — promoted to primary surface. Per-student card with
            full target text and pill buttons (faster for TAs than dropdowns). */}
        {(intervention?.targets?.length || 0) > 0 ? (
          <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-800">Target Scoring</h3>
              <span className="text-xs text-gray-500">Per student, per target</span>
            </div>
            <div className="space-y-4">
              {(sessionData.targetScores || [])
                .filter(ts => (sessionData.attendance || []).find(a => a.studentId === ts.studentId && a.present))
                .map(ts => (
                <div key={ts.studentId} className="bg-white border border-gray-200 rounded-md shadow-sm">
                  <div className="px-3 py-2 bg-gray-50 font-medium text-gray-800 rounded-t-md">
                    {ts.name}
                  </div>
                  <div className="p-3 space-y-3">
                    {ts.scores.map(score => (
                      <div key={score.targetId} className="space-y-2">
                        <p className="text-sm text-gray-800 leading-snug">
                          <span className="text-gray-500">Target:</span> {score.targetText}
                        </p>
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Status for ${ts.name}`}>
                          {[
                            { value: 'not_met', label: 'Not met', activeClasses: 'bg-red-600 text-white border-red-600', idleClasses: 'bg-white text-red-700 border-red-300 hover:bg-red-50' },
                            { value: 'partially_met', label: 'Partially met', activeClasses: 'bg-amber-500 text-white border-amber-500', idleClasses: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50' },
                            { value: 'achieved', label: 'Achieved', activeClasses: 'bg-green-600 text-white border-green-600', idleClasses: 'bg-white text-green-700 border-green-300 hover:bg-green-50' },
                          ].map(opt => {
                            const isActive = score.status === opt.value;
                            return (
                              <button
                                type="button"
                                key={opt.value}
                                role="radio"
                                aria-checked={isActive}
                                onClick={() => handleTargetScoreChange(ts.studentId, score.targetId, opt.value)}
                                className={`text-sm px-3 py-1.5 border rounded-full font-medium transition-colors ${isActive ? opt.activeClasses : opt.idleClasses}`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-4 text-sm text-amber-900">
            <p className="font-medium mb-1">No SMART targets set for this intervention.</p>
            <p className="text-amber-800">Without targets, sessions cannot be scored per student. Ask the SENCO to add 3 SMART targets in the Intervention Dashboard, or use the "Suggest SMART targets with AI" button in the edit-targets flow.</p>
          </div>
        )}

        {/* Activities Completed — kept visible (required), reduced to single line */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Activities Completed *
          </label>
          <input
            type="text"
            value={sessionData.activities}
            onChange={(e) => setSessionData(prev => ({ ...prev, activities: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Read Set 2 sounds, ten flashcards, sentence dictation."
            required
          />
        </div>

        {/* Free-text fields demoted into an optional collapsed block */}
        <details className="border border-gray-200 rounded-md">
          <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 select-none">
            Optional context (only fill if something notable happened)
          </summary>
          <div className="p-4 space-y-4 border-t border-gray-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Progress Made
              </label>
              <textarea
                value={sessionData.progress}
                onChange={(e) => setSessionData(prev => ({ ...prev, progress: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What progress was made toward intervention goals?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Challenges Encountered
              </label>
              <textarea
                value={sessionData.challenges}
                onChange={(e) => setSessionData(prev => ({ ...prev, challenges: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any challenges or difficulties during the session?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Steps
              </label>
              <textarea
                value={sessionData.nextSteps}
                onChange={(e) => setSessionData(prev => ({ ...prev, nextSteps: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What should be focused on in the next session?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes
              </label>
              <textarea
                value={sessionData.notes}
                onChange={(e) => setSessionData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Any other observations or notes..."
              />
            </div>
          </div>
        </details>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => {
              setSessionData({
                date: new Date().toISOString().split('T')[0],
                time: new Date().toTimeString().slice(0, 5),
                duration: '',
                attendance: students.map(student => ({
                  studentId: student.id,
                  present: true,
                  name: `${student.firstName} ${student.lastName}`
                })),
                activities: '',
                progress: '',
                challenges: '',
                nextSteps: '',
                notes: '',
                targetScores: students.map(student => ({
                  studentId: student.id,
                  name: `${student.firstName} ${student.lastName}`,
                  scores: (intervention?.targets || []).slice(0, 3).map(t => ({
                    targetId: t.id,
                    targetText: t.text,
                    status: 'not_met'
                  }))
                }))
              });
            }}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Reset
          </button>
          <button
            type="submit"
            disabled={!eligibleDate && !hasRole(role, ROLES.SENCO)}
            className={`px-6 py-2 text-white rounded-md transition-colors ${(!eligibleDate && !hasRole(role, ROLES.SENCO)) ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
          >
            Log Session
          </button>
        </div>
      </form>
    </div>
  );
};

export default SessionLogForm; 
